import React, {useRef, useCallback, forwardRef} from 'react';
import PropTypes from 'prop-types';

import ReactDatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';

import { StyledDatepicker, IconWrapper } from './DatePicker.styles';

import ru from 'date-fns/locale/ru';

registerLocale('ru', ru);
setDefaultLocale('ru', ru);

const propTypes = {
    className: PropTypes.string,
    width: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
        PropTypes.object,
    ]),
    fluid: PropTypes.bool,
    dateFormat: PropTypes.string,
    placeholderText: PropTypes.string,
    filterDate: PropTypes.func,
    disabled: PropTypes.bool,
    clearable: PropTypes.bool,
    error: PropTypes.bool,
    showTimeSelect: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    compact: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};

const DatePicker = ({
    className = '',
    width = '9.285714285714286rem',
    name,
    value: selected,
    onChange,
    fluid,
    clearable,
    error,
    dateFormat = 'EEEEEE, dd.MM.yy',
    placeholderText = 'пн, дд.мм.гг',
    filterDate,
    showTimeSelect,
    minDate,
    maxDate,
    compact,
    disabled,
    hideCalendarIcon,
    valueText,
    ...rest
}) => {
    const dpRef = useRef(null);

    const handleChange = useCallback(
        (date, e) => {
            if (!clearable && !date) return;

            onChange(e, {
                name,
                value: date
            });
        },
        [onChange, clearable]
    );

    const handleIconClick = () => {
        const el = ((dpRef || {}).current || {}).input;

        if (!el) return;

        el.focus();
    };

    return (
        <StyledDatepicker
            {...fluid
                ? { fluid: true }
                : { style: { width } }
            }
            className={
                `datepicker-container ${className} ${error ? 'error' : ''}` +
                `${showTimeSelect ? ' datepicker-container--with-time' : ''}` +
                `${disabled ? ' disabled' : ''}` +
                `${compact ? ' compact' : ''}`
            }
        >
            <ReactDatePicker
                {...(filterDate ? { filterDate } : {})}
                {...(disabled ? { tabIndex: -1 } : {})}
                disabled={disabled}
                dateFormat={dateFormat}
                placeholderText={placeholderText}
                showTimeSelect={showTimeSelect}
                minDate={minDate || new Date(2010, 1, 1)}
                maxDate={maxDate || new Date(2100, 1, 1)}
                {...rest}
                ref={dpRef}
                selected={selected}
                onChange={handleChange}
                isClearable={clearable}
                calendarStartDay={1}
                value={valueText}
            />
            {!!(hideCalendarIcon || (clearable && selected))
                ? null
                : <IconWrapper onClick={disabled ? null : handleIconClick}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.10071 7.72364C5.10071 7.54827 4.95846 7.40619 4.78294 7.40619H3.67404C3.49872 7.40619 3.35645 7.54827 3.35645 7.72364V8.8324C3.35645 9.008 3.49872 9.15014 3.67404 9.15014H4.78294C4.95846 9.15014 5.10071 9.008 5.10071 8.8324V7.72364Z" fill="#135CA9"/>
                        <path d="M7.87202 7.72364C7.87202 7.54827 7.72974 7.40619 7.55457 7.40619H6.44553C6.27021 7.40619 6.12793 7.54827 6.12793 7.72364V8.8324C6.12793 9.008 6.27021 9.15014 6.44553 9.15014H7.55457C7.72974 9.15014 7.87202 9.008 7.87202 8.8324V7.72364Z" fill="#135CA9"/>
                        <path d="M10.6437 7.72364C10.6437 7.54827 10.5014 7.40619 10.3261 7.40619H9.21718C9.04166 7.40619 8.89941 7.54827 8.89941 7.72364V8.8324C8.89941 9.008 9.04166 9.15014 9.21718 9.15014H10.3261C10.5014 9.15014 10.6437 9.008 10.6437 8.8324V7.72364Z" fill="#135CA9"/>
                        <path d="M5.10071 10.4954C5.10071 10.3197 4.95846 10.1778 4.78294 10.1778H3.67404C3.49872 10.1778 3.35645 10.3197 3.35645 10.4954V11.604C3.35645 11.7794 3.49872 11.9215 3.67404 11.9215H4.78294C4.95846 11.9215 5.10071 11.7794 5.10071 11.604V10.4954Z" fill="#135CA9"/>
                        <path d="M7.87202 10.4954C7.87202 10.3197 7.72974 10.1778 7.55457 10.1778H6.44553C6.27021 10.1778 6.12793 10.3197 6.12793 10.4954V11.604C6.12793 11.7794 6.27021 11.9215 6.44553 11.9215H7.55457C7.72974 11.9215 7.87202 11.7794 7.87202 11.604V10.4954Z" fill="#135CA9"/>
                        <path d="M10.6437 10.4954C10.6437 10.3197 10.5014 10.1778 10.3262 10.1778H9.21718C9.04166 10.1778 8.89941 10.3197 8.89941 10.4954V11.604C8.89941 11.7794 9.04166 11.9215 9.21718 11.9215H10.3262C10.5014 11.9215 10.6437 11.7794 10.6437 11.604V10.4954Z" fill="#135CA9"/>
                        <path d="M12.6264 1.55875V3.25228C12.6264 4.01774 12.0054 4.63458 11.2401 4.63458H10.3656C9.60015 4.63458 8.97095 4.01774 8.97095 3.25228V1.55267H5.02923V3.25228C5.02923 4.01774 4.40005 4.63458 3.63477 4.63458H2.76007C1.99475 4.63458 1.37381 4.01774 1.37381 3.25228V1.55875C0.704918 1.57892 0.155273 2.1324 0.155273 2.81271V12.7376C0.155273 13.4307 0.717049 13.9999 1.41015 13.9999H12.59C13.2821 13.9999 13.8449 13.4295 13.8449 12.7376V2.81271C13.8449 2.1324 13.2953 1.57892 12.6264 1.55875ZM12.2202 12.1196C12.2202 12.4191 11.9773 12.6621 11.6777 12.6621H2.29859C1.99894 12.6621 1.75608 12.4191 1.75608 12.1196V6.99299C1.75608 6.69335 1.99891 6.45035 2.29859 6.45035H11.6777C11.9773 6.45035 12.2202 6.69335 12.2202 6.99299L12.2202 12.1196Z" fill="#459EFF"/>
                        <path d="M2.75719 3.72677H3.62222C3.88478 3.72677 4.09767 3.5142 4.09767 3.25163V0.475307C4.09767 0.212717 3.88478 0 3.62222 0H2.75719C2.4946 0 2.28174 0.212717 2.28174 0.475307V3.25163C2.28174 3.5142 2.4946 3.72677 2.75719 3.72677Z" fill="#135CA9"/>
                        <path d="M10.3543 3.72677H11.2194C11.4818 3.72677 11.6946 3.5142 11.6946 3.25163V0.475307C11.6947 0.212717 11.4818 0 11.2194 0H10.3543C10.0918 0 9.87891 0.212717 9.87891 0.475307V3.25163C9.87891 3.5142 10.0918 3.72677 10.3543 3.72677Z" fill="#135CA9"/>
                    </svg>
                </IconWrapper>
            }
        </StyledDatepicker>
    );
};

DatePicker.propTypes = propTypes;
export default DatePicker;



// WEBPACK FOOTER //
// ./App/components/DatePicker/DatePicker.jsx